import type { TablePaginationConfig } from 'antd/lib/table/interface';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import useDebouncedValue from '@/hooks/useDebouncedValue';
import { useAppSelector } from '@/store';

interface IProps {
  defaultUserKey?: string;
  searchStorageKey?: string;
}

const useFilters = (props?: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const params = new URLSearchParams(searchParams);

  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const searchValue = useMemo(
    () =>
      searchParams.get('search') ||
      (props?.searchStorageKey
        ? localStorage.getItem(props.searchStorageKey)
        : '') ||
      '',
    [searchParams]
  );
  const debouncedSearchValue = useDebouncedValue(searchValue);

  // Get pagination info from url
  const paginationData = useMemo(() => {
    return {
      currentPage: Number(params.get('page') || 1),
      itemsPerPage: Number(params.get('per-page') || 10)
    };
  }, [searchParams]);

  // Get selected user from localStorage
  useEffect(() => {
    const userSearchStorage = localStorage.getItem(`${location.pathname}-user`);
    if (userSearchStorage) {
      setSelectedUser(userSearchStorage);
    } else {
      setSelectedUser(props?.defaultUserKey || '-1');
    }
  }, [userInfo]);

  const handleFiltersChange = (pagination: TablePaginationConfig) => {
    params.set('page', String(pagination.current || 1));
    params.set('per-page', String(pagination.pageSize || 10));
    setSearchParams(params);
  };

  const handleUserChange = (
    optionValue: string,
    params = new URLSearchParams(location.search),
    ignorePaginationUpdate = false
  ) => {
    setSelectedUser(optionValue);
    localStorage.setItem(`${location.pathname}-user`, optionValue);

    if (!ignorePaginationUpdate) {
      params.set('page', '1');
      params.set('per-page', paginationData.itemsPerPage.toString() || '10');
    }

    setSearchParams(params);
  };

  const handleSearch = (value: string) => {
    if (value === '') {
      params.delete('search');
      props?.searchStorageKey &&
        localStorage.removeItem(props.searchStorageKey);
    } else {
      params.set('search', value);
      props?.searchStorageKey &&
        localStorage.setItem(props.searchStorageKey, value);
    }
    params.set('page', '1');
    params.set('per-page', '10');
    setSearchParams(params);
  };

  return {
    paginationData,
    selectedUser,
    searchValue,
    debouncedSearchValue,
    handleFiltersChange,
    handleUserChange,
    handleSearch
  };
};

export default useFilters;
